import { FC, PropsWithChildren, useEffect } from "react";
import { useSelector } from "react-redux";
import useAuth from "./hooks/useAuth";
import Cookies from "js-cookie";
import { RootState } from "@/store";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import useOpenId from "./hooks/useOpenId";
import useToken from "./hooks/useToken";
import useClientId from "./hooks/useClientId";
import LoadingView from "../LoadingView";
import { createSearchParams, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ErrorComponentPage from "../Error/ErrorComponentPage";

interface IAuthenticationProvider extends PropsWithChildren {
  path?: string;
  protection: "private" | "public" | "unprotected";
}

// This provider checks the url and the current cookies for authentication
export const AuthenticationProvider: FC<IAuthenticationProvider> = ({
  path="",
  protection,
  children
}) => {
  
  const { 
    token, clientId, secret, code, state, ...restParams
  } = useParams();
  const [searchParams, _setSearchParams] = useSearchParams();

  const Settings = useSettingsContext();  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginNotMade, loginRefresh } = useAuth();
  const { loginToken } = useToken();
  const { loginClientId } = useClientId();
  const { redirectOpenId, loginOpenId } = useOpenId();

  const isAuthenticated = useSelector((store: RootState) => store.auth.isAuthenticated);
  const isAuthenticating = useSelector((store: RootState) => store.auth.isAuthenticating);
  const loginType = useSelector((store: RootState) => store.auth.type);
  const loginStatusType = useSelector((store: RootState) => store.auth.statusType);
  const loginStatusCode = useSelector((store: RootState) => store.auth.statusCode);

  const cookiesToken = Cookies.get("token");

  const handleRedirectLogIn = () => {
    const {
      redirectPath, pathParams, ...restSearchParams
    } = Object.fromEntries(searchParams);

    if (redirectPath) {
      navigate({
        pathname: t(redirectPath, pathParams ? JSON.parse(pathParams) : {}) as string,
        search: createSearchParams(restSearchParams).toString()
      });
    } else {
      navigate(t(`common:url.${Settings.homepage ?? "invoices"}`));
    }
  }

  const handleRedirectLogOut = () => {
    navigate({
      pathname: t("common:url.login"),
      search: createSearchParams({
        ...(path && {redirectPath: path}),
        ...(!!Object.entries(restParams).length && {pathParams: JSON.stringify(restParams)}),
        ...(!!searchParams.toString() && Object.fromEntries(searchParams))
      }).toString()
    });
  }

  const handleLoadingView = (children: React.ReactNode) => {

    if ( 
      loginStatusType == "error" && 
      !(loginType == "credentials") 
    ) {
      return <ErrorComponentPage status={loginStatusCode} />
    }

    if (isAuthenticating) {

      if ( 
        // Quan s'inicia sessió per credencials cal ensenyar el loader
        // de la vista de credencials i no el loader global
        path == "common:url.login" && loginType == "credentials"
      ) {
        return children;
      }

    } else {

      if (isAuthenticated) {

        if (
          protection == "private" ||
          protection == "unprotected"
        ) {
          return children;
        }

        if ( 
          protection == "public" &&

          // Login handles redirects by itself
          !(loginType == "credentials" && path == "common:url.login")
        ) {
          handleRedirectLogIn();
        }

      }

      if (!isAuthenticated) {
        if (
          protection == "public" || 
          protection == "unprotected"
        ) {
          return children;
        }

        if (

          // Any route
          // OIDC handles redirections itself
          (protection == "private" && !Settings.features?.oidc) || 

          // LoginNotMade
          loginType == "none"
        ) {

          handleRedirectLogOut();
        }
      }

    }

    return <LoadingView />
  }

  // De moment sembla que això funciona, però potser es podria millorar 
  // treient-ho del hook i passant-ho a les funcions de sobre. O, inclús,
  // potser es podria moure a un altre provider més amunt perquè es comprovi
  // l'inici de sessió per searchParams només un cop i no a cada canvi de ruta.
  useEffect(() => {
    // Token searchParams
    if ( searchParams.get("token") ) {
      loginToken({token: searchParams.get("token")!});

      // ClientID & Secret searchParams
    } else if ( searchParams.get("id_client") && searchParams.get("secret") ) {
      loginClientId({clientId: searchParams.get("id_client")!, secret: searchParams.get("secret")!});

    } else if (!isAuthenticated) {
      
      // Already logged in with stored session token
      if ( cookiesToken ) {
        loginRefresh(cookiesToken);

      // Code, Nonce & Secret searchParams
      } else if ( Settings.features?.oidc && (!searchParams.get("code") || !searchParams.get("state")) && !(protection == "unprotected")) {
        redirectOpenId(path, searchParams);
      } else if ( Settings.features?.oidc && searchParams.get("code") && searchParams.get("state") ) {
        loginOpenId({code: searchParams.get("code")!, state: searchParams.get("state")!});
      } else {
        loginNotMade();
      }
    }
    
  }, []);

  return handleLoadingView(children);
}